import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp.js';
import { bookingService } from '../services/bookingService';
import { chatService } from '../services/chatService';
import toast from 'react-hot-toast';
import { 
  ArrowLeft,
  Briefcase,
  User,
  Calendar,
  DollarSign,
  Phone,
  Video,
  MapPin,
  CheckCircle,
  Clock,
  Check,
  X,
  AlertTriangle,
  MessageSquare,
  MessageCircle,
  Zap,
  Mail,
  Shield,
  Loader2,
  Eye,
  Star
} from 'lucide-react';
import dataSyncService from '../services/dataSyncService'; // Added import for dataSyncService
import { completeConsultation } from '../services/consultationService';

const LawyerCases = () => {
  const navigate = useNavigate();
  const { user, userRole } = useApp();
  const isAdmin = userRole === 'admin';
  const isLawyer = userRole === 'lawyer';
  
  // Translations
  const translations = {
    english: {
      activeCases: 'Active Cases',
      pendingCases: 'Pending Cases',
      recentCompletedCases: 'Recent Completed Cases',
      noCases: 'No cases found',
      loadingCases: 'Loading cases...',
      acceptBooking: 'Accept Booking',
      rejectBooking: 'Reject Booking',
      completeConsultation: 'Complete Consultation',
      viewClientProfile: 'View Client Profile',
      startChat: 'Start Chat',
      bookingAccepted: 'Booking accepted successfully!',
      bookingRejected: 'Booking rejected successfully!',
      consultationCompleted: 'Consultation completed successfully!',
      errorAccepting: 'Error accepting booking',
      errorRejecting: 'Error rejecting booking',
      errorCompleting: 'Error completing consultation'
    },
    hindi: {
      activeCases: 'सक्रिय मामले',
      pendingCases: 'लंबित मामले',
      recentCompletedCases: 'हाल ही में पूर्ण किए गए मामले',
      noCases: 'कोई मामला नहीं मिला',
      loadingCases: 'मामले लोड हो रहे हैं...',
      acceptBooking: 'बुकिंग स्वीकार करें',
      rejectBooking: 'बुकिंग अस्वीकार करें',
      completeConsultation: 'परामर्श पूरा करें',
      viewClientProfile: 'ग्राहक प्रोफ़ाइल देखें',
      startChat: 'चैट शुरू करें',
      bookingAccepted: 'बुकिंग सफलतापूर्वक स्वीकार की गई!',
      bookingRejected: 'बुकिंग सफलतापूर्वक अस्वीकार की गई!',
      consultationCompleted: 'परामर्श सफलतापूर्वक पूरा हुआ!',
      errorAccepting: 'बुकिंग स्वीकार करने में त्रुटि',
      errorRejecting: 'बुकिंग अस्वीकार करने में त्रुटि',
      errorCompleting: 'परामर्श पूरा करने में त्रुटि'
    }
  };
  
  const t = translations.english; // Default to English
  const [activeTab, setActiveTab] = useState('all');
  const [showClientProfile, setShowClientProfile] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState({
    active: [],
    pending: [],
    completed: [],
    rescheduled: [],
    emergency: []
  });

  // Delete old completed cases (older than 24 hours)
  const deleteOldCompletedCases = useCallback(async () => {
    try {
      console.log('🗑️ Checking for old completed cases to delete...');
      const result = await bookingService.deleteOldCompletedCases();
      if (result.success) {
        console.log(`✅ Deleted ${result.deletedCount} old completed cases`);
      } else {
        console.error('❌ Error deleting old cases:', result.error);
      }
    } catch (error) {
      console.error('❌ Error in deleteOldCompletedCases:', error);
    }
  }, []);

  // Function to categorize cases by status
  const categorizeCases = (casesArray) => {
    const categorized = {
      active: [],
      pending: [],
      completed: [],
      rescheduled: [],
      emergency: []
    };
    
    casesArray.forEach(caseData => {
      const status = caseData.status?.toLowerCase() || 'pending';
      if (status === 'active' || status === 'confirmed' || status === 'ongoing') {
        categorized.active.push(caseData);
      } else if (status === 'pending' || status === 'waiting') {
        categorized.pending.push(caseData);
      } else if (status === 'completed' || status === 'finished' || status === 'done') {
        categorized.completed.push(caseData);
      } else if (status === 'rescheduled' || status === 'postponed') {
        categorized.rescheduled.push(caseData);
      } else if (status === 'emergency' || status === 'urgent') {
        categorized.rescheduled.push(caseData);
      } else {
        categorized.pending.push(caseData); // Default to pending
      }
    });
    
    return categorized;
  };

  // Load lawyer cases from database
  const loadLawyerCases = useCallback(async () => {
    if (!user?.uid) {
      console.log('❌ No user UID available');
      console.log('User object:', user);
      return;
    }

    try {
      setLoading(true);
      console.log('🔄 Loading lawyer cases for user:', user.uid);
      
      // First, load from localStorage for immediate display
      const lawyerCasesKey = `lawyerCases_${user.uid}`;
      const lawyerBookingsKey = `lawyerBookings_${user.uid}`;
      const generalBookingsKey = `bookings_${user.uid}`;
      
      // Load from multiple localStorage sources
      const localStorageCases = JSON.parse(localStorage.getItem(lawyerCasesKey) || '[]');
      const localStorageBookings = JSON.parse(localStorage.getItem(lawyerBookingsKey) || '[]');
      const generalBookings = JSON.parse(localStorage.getItem(generalBookingsKey) || '[]');
      const oldBookings = JSON.parse(localStorage.getItem('bookings') || '[]');
      
      // Combine all localStorage sources
      let allLocalCases = [...localStorageCases, ...localStorageBookings, ...generalBookings, ...oldBookings];
      
      // Filter for this lawyer's cases
      allLocalCases = allLocalCases.filter(caseData => 
        caseData.lawyerId === user.uid || 
        caseData.lawyer_id === user.uid ||
        caseData.lawyer?.id === user.uid
      );
      
      // Remove duplicates
      allLocalCases = allLocalCases.filter((caseData, index, self) => 
        index === self.findIndex(c => c.id === caseData.id)
      );
      
      console.log('📱 Found localStorage cases:', allLocalCases.length);
      
      // Set cases immediately from localStorage for instant display
      if (allLocalCases.length > 0) {
        const categorizedLocalCases = categorizeCases(allLocalCases);
        setCases(categorizedLocalCases);
        console.log('📱 Set cases from localStorage:', categorizedLocalCases);
        setLoading(false); // Stop loading since we have data
      }
      
      // Add mobile-specific optimizations
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (isMobile) {
        console.log('📱 Mobile device detected, applying optimizations');
      }
      
      // First, delete old completed cases
      await deleteOldCompletedCases();
      console.log('User role:', user.role);
      console.log('Is lawyer:', isLawyer);

      // Fix data inconsistencies before loading cases
      console.log('🔧 Fixing data inconsistencies...');
      try {
        const syncResult = await dataSyncService.fixInconsistentClientIds();
        if (syncResult.success) {
          console.log('✅ Data inconsistencies fixed:', syncResult.message);
        } else {
          console.log('⚠️ Some data inconsistencies could not be fixed:', syncResult.error);
        }
      } catch (syncError) {
        console.error('❌ Error during data sync:', syncError);
      }

      // First, let's check if there are any bookings at all in the database
      console.log('🔍 Checking all bookings in database...');
      const allBookingsResult = await bookingService.getAllBookings();
      if (allBookingsResult.success) {
        console.log('📊 Total bookings in database:', allBookingsResult.data.length);
        console.log('📋 Sample booking data:', allBookingsResult.data.slice(0, 2));
        console.log('📋 Sample booking fields:', allBookingsResult.data[0] ? Object.keys(allBookingsResult.data[0]) : 'No bookings');
        console.log('📋 Sample booking client fields:', allBookingsResult.data[0] ? {
          clientName: allBookingsResult.data[0]?.clientName,
          full_name: allBookingsResult.data[0]?.full_name,
          userName: allBookingsResult.data[0]?.userName,
          userId: allBookingsResult.data[0]?.userId,
          client_name: allBookingsResult.data[0]?.client_name
        } : 'No bookings');
        
        // Check if any bookings have this lawyer as the lawyerId
        const lawyerBookings = allBookingsResult.data.filter(booking => 
          booking.lawyerId === user.uid || 
          booking.lawyer_id === user.uid ||
          booking.lawyer?.id === user.uid
        );
        console.log('👨‍💼 Bookings for this lawyer:', lawyerBookings.length);
        console.log('🔍 Lawyer bookings data:', lawyerBookings);
      } else {
        console.error('❌ Error getting all bookings:', allBookingsResult.error);
      }

      // Load pending cases
      console.log('🔄 Loading pending cases...');
      const pendingResult = await bookingService.getLawyerPendingCases(user.uid);
      if (pendingResult.success) {
        console.log('✅ Loaded pending cases:', pendingResult.data.length);
        console.log('📋 Pending cases data:', pendingResult.data);
        
        // Enhance pending cases with client profile information
        const enhancedPendingCases = await Promise.all(
          pendingResult.data.map(async (caseData) => {
            try {
              if (caseData.userId) {
                console.log('🔍 Fetching client profile for pending case:', caseData.userId);
                const { doc, getDoc } = await import('firebase/firestore');
                const { db } = await import('../config/firebase');
                
                const clientDoc = await getDoc(doc(db, 'users', caseData.userId));
                if (clientDoc.exists()) {
                  const clientData = clientDoc.data();
                  console.log('✅ Client profile found for pending case:', clientData);
                  
                  return {
                    ...caseData,
                    clientName: clientData.full_name || clientData.displayName || clientData.name || 'Client',
                    clientPhoto: clientData.profile_picture || clientData.profilePic || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=40&h=40&fit=crop&crop=face',
                    clientCity: clientData.custom_city || clientData.city || 'Location not available'
                  };
                }
              }
              return caseData;
            } catch (error) {
              console.error('❌ Error fetching client profile for pending case:', error);
              return caseData;
            }
          })
        );
        
        console.log('📋 Enhanced pending cases:', enhancedPendingCases);
        setCases(prev => ({
          ...prev,
          pending: enhancedPendingCases
        }));
      } else {
        console.error('❌ Error loading pending cases:', pendingResult.error);
        setCases(prev => ({ ...prev, pending: [] }));
      }

      // Load active cases
      console.log('🔄 Loading active cases...');
      const activeResult = await bookingService.getLawyerActiveCases(user.uid);
      if (activeResult.success) {
        console.log('✅ Loaded active cases:', activeResult.data.length);
        console.log('📋 Active cases data:', activeResult.data);
        
        // Enhance active cases with client profile information
        const enhancedActiveCases = await Promise.all(
          activeResult.data.map(async (caseData) => {
            try {
              // Check for both clientId and userId fields
              const clientId = caseData.clientId || caseData.userId;
              console.log('🔍 Fetching client profile for active case:', clientId);
              console.log('🔍 Case data fields:', Object.keys(caseData));
              console.log('🔍 Case data:', caseData);
              
              if (clientId) {
                const { doc, getDoc } = await import('firebase/firestore');
                const { db } = await import('../config/firebase');
                
                const clientDoc = await getDoc(doc(db, 'users', clientId));
                if (clientDoc.exists()) {
                  const clientData = clientDoc.data();
                  console.log('✅ Client profile found for active case:', clientData);
                  
                  // Try multiple field names for client name
                  const clientName = clientData.full_name || 
                                   clientData.fullName || 
                                   clientData.displayName || 
                                   clientData.name || 
                                   (clientData.firstName && clientData.lastName ? `${clientData.firstName} ${clientData.lastName}` : null) ||
                                   'Client';
                  
                  // Try multiple field names for profile picture
                  const clientPhoto = clientData.profile_picture || 
                                    clientData.profilePic || 
                                    clientData.photoURL || 
                                    clientData.avatar || 
                                    'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=40&h=40&fit=crop&crop=face';
                  
                  console.log('✅ Enhanced client data:', { clientName, clientPhoto });
                  
                  return {
                    ...caseData,
                    clientName: clientName,
                    clientPhoto: clientPhoto,
                    clientCity: clientData.custom_city || clientData.city || 'Location not available'
                  };
                } else {
                  console.log('❌ Client document not found for ID:', clientId);
                }
              } else {
                console.log('❌ No clientId or userId found in case data');
              }
              return caseData;
            } catch (error) {
              console.error('❌ Error fetching client profile for active case:', error);
              return caseData;
            }
          })
        );
        
        console.log('📋 Enhanced active cases:', enhancedActiveCases);
        setCases(prev => ({
          ...prev,
          active: enhancedActiveCases
        }));
      } else {
        console.error('❌ Error loading active cases:', activeResult.error);
        setCases(prev => ({ ...prev, active: [] }));
      }

      // Load completed cases
      console.log('🔄 Loading completed cases...');
      const completedResult = await bookingService.getLawyerCompletedCases(user.uid);
      if (completedResult.success) {
        console.log('✅ Loaded completed cases:', completedResult.data.length);
        console.log('📋 Completed cases data:', completedResult.data);
        
        // Enhance completed cases with client profile information
        const enhancedCompletedCases = await Promise.all(
          completedResult.data.map(async (caseData) => {
            try {
              // Check for both clientId and userId fields
              const clientId = caseData.clientId || caseData.userId;
              console.log('🔍 Fetching client profile for completed case:', clientId);
              console.log('🔍 Case data fields:', Object.keys(caseData));
              console.log('🔍 Case data:', caseData);
              
              if (clientId) {
                const { doc, getDoc } = await import('firebase/firestore');
                const { db } = await import('../config/firebase');
                
                const clientDoc = await getDoc(doc(db, 'users', clientId));
                if (clientDoc.exists()) {
                  const clientData = clientDoc.data();
                  console.log('✅ Client profile found for completed case:', clientData);
                  
                  // Try multiple field names for client name
                  const clientName = clientData.full_name || 
                                   clientData.fullName || 
                                   clientData.displayName || 
                                   clientData.name || 
                                   (clientData.firstName && clientData.lastName ? `${clientData.firstName} ${clientData.lastName}` : null) ||
                                   'Client';
                  
                  // Try multiple field names for profile picture
                  const clientPhoto = clientData.profile_picture || 
                                    clientData.profilePic || 
                                    clientData.photoURL || 
                                    clientData.avatar || 
                                    'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=40&h=40&fit=crop&crop=face';
                  
                  console.log('✅ Enhanced client data:', { clientName, clientPhoto });
                  
                  return {
                    ...caseData,
                    clientName: clientName,
                    clientPhoto: clientPhoto,
                    clientCity: clientData.custom_city || clientData.city || 'Location not available'
                  };
                } else {
                  console.log('❌ Client document not found for ID:', clientId);
                }
              } else {
                console.log('❌ No clientId or userId found in case data');
              }
              return caseData;
            } catch (error) {
              console.error('❌ Error fetching client profile for completed case:', error);
              return caseData;
            }
          })
        );
        
        console.log('📋 Enhanced completed cases:', enhancedCompletedCases);
        setCases(prev => ({
          ...prev,
          completed: enhancedCompletedCases
        }));
      } else {
        console.error('❌ Error loading completed cases:', completedResult.error);
        setCases(prev => ({ ...prev, completed: [] }));
      }

      // Log final cases state
      console.log('📊 Final cases state:', {
        pending: pendingResult.success ? pendingResult.data.length : 0,
        active: activeResult.success ? activeResult.data.length : 0,
        completed: completedResult.success ? completedResult.data.length : 0
      });

      // If no cases found, offer to create a test case
      const totalCases = (pendingResult.success ? pendingResult.data.length : 0) + 
                        (activeResult.success ? activeResult.data.length : 0) + 
                        (completedResult.success ? completedResult.data.length : 0);
      
      if (totalCases === 0) {
        console.log('⚠️ No cases found for this lawyer');
        console.log('💡 Check if the lawyer ID matches the bookings in the database');
      }

    } catch (error) {
      console.error('❌ Error loading lawyer cases:', error);
      toast.error('Error loading cases');
    } finally {
      setLoading(false);
    }
  }, [user?.uid, user?.role, isLawyer]);

  // Load cases from database on component mount
  useEffect(() => {
    console.log('🔄 useEffect triggered - isLawyer:', isLawyer, 'user?.uid:', user?.uid);
    if (isLawyer && user?.uid) {
      console.log('✅ Conditions met, loading lawyer cases...');
      loadLawyerCases();
    } else {
      console.log('❌ Conditions not met - isLawyer:', isLawyer, 'user?.uid:', user?.uid);
      setLoading(false);
    }
  }, [isLawyer, user?.uid, loadLawyerCases]);

  // Real-time listener for cases updates
  useEffect(() => {
    if (!isLawyer || !user?.uid) return;

    console.log('🔗 Setting up real-time listeners for cases...');
    
    let unsubscribePending = null;
    let unsubscribeActive = null;
    let unsubscribeCompleted = null;

    const setupRealTimeListeners = async () => {
      try {
        const { collection, query, where, onSnapshot } = await import('firebase/firestore');
        const { db } = await import('../config/firebase');

        // Listen to pending cases
        const pendingQuery = query(
          collection(db, 'bookings'),
          where('lawyerId', '==', user.uid),
          where('status', '==', 'pending')
        );
        unsubscribePending = onSnapshot(pendingQuery, async (snapshot) => {
          console.log('📡 Real-time pending cases update:', snapshot.docs.length, 'cases');
          const pendingCases = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          // Enhance with client profiles
          const enhancedPendingCases = await Promise.all(
            pendingCases.map(async (caseData) => {
              try {
                if (caseData.userId) {
                  const { doc, getDoc } = await import('firebase/firestore');
                  const clientDoc = await getDoc(doc(db, 'users', caseData.userId));
                  if (clientDoc.exists()) {
                    const clientData = clientDoc.data();
                    return {
                      ...caseData,
                      clientName: clientData.full_name || clientData.displayName || clientData.name || 'Client',
                      clientPhoto: clientData.profile_picture || clientData.profilePic || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=40&h=40&fit=crop&crop=face',
                      clientCity: clientData.custom_city || clientData.city || 'Location not available'
                    };
                  }
                }
                return caseData;
              } catch (error) {
                console.error('❌ Error enhancing pending case:', error);
                return caseData;
              }
            })
          );
          
          setCases(prev => ({ ...prev, pending: enhancedPendingCases }));
          
          // Save to localStorage for persistence
          const allCases = {
            ...prev,
            pending: enhancedPendingCases
          };
          localStorage.setItem(`lawyerCases_${user.uid}`, JSON.stringify(Object.values(allCases).flat()));
        });

        // Listen to active cases
        const activeQuery = query(
          collection(db, 'bookings'),
          where('lawyerId', '==', user.uid),
          where('status', '==', 'active')
        );
        unsubscribeActive = onSnapshot(activeQuery, async (snapshot) => {
          console.log('📡 Real-time active cases update:', snapshot.docs.length, 'cases');
          const activeCases = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          // Enhance with client profiles
          const enhancedActiveCases = await Promise.all(
            activeCases.map(async (caseData) => {
              try {
                const clientId = caseData.clientId || caseData.userId;
                if (clientId) {
                  const { doc, getDoc } = await import('firebase/firestore');
                  const clientDoc = await getDoc(doc(db, 'users', clientId));
                  if (clientDoc.exists()) {
                    const clientData = clientDoc.data();
                    const clientName = clientData.full_name || 
                                     clientData.fullName || 
                                     clientData.displayName || 
                                     clientData.name || 
                                     (clientData.firstName && clientData.lastName ? `${clientData.firstName} ${clientData.lastName}` : null) ||
                                     'Client';
                    const clientPhoto = clientData.profile_picture || 
                                      clientData.profilePic || 
                                      clientData.photoURL || 
                                      clientData.avatar || 
                                      'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=40&h=40&fit=crop&crop=face';
                    
                    return {
                      ...caseData,
                      clientName: clientName,
                      clientPhoto: clientPhoto,
                      clientCity: clientData.custom_city || clientData.city || 'Location not available'
                    };
                  }
                }
                return caseData;
              } catch (error) {
                console.error('❌ Error enhancing active case:', error);
                return caseData;
              }
            })
          );
          
          setCases(prev => ({ ...prev, active: enhancedActiveCases }));
          
          // Save to localStorage for persistence
          const allCases = {
            ...prev,
            active: enhancedActiveCases
          };
          localStorage.setItem(`lawyerCases_${user.uid}`, JSON.stringify(Object.values(allCases).flat()));
        });

        // Listen to completed cases
        const completedQuery = query(
          collection(db, 'bookings'),
          where('lawyerId', '==', user.uid),
          where('status', '==', 'completed')
        );
        unsubscribeCompleted = onSnapshot(completedQuery, async (snapshot) => {
          console.log('📡 Real-time completed cases update:', snapshot.docs.length, 'cases');
          const completedCases = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          // Enhance with client profiles
          const enhancedCompletedCases = await Promise.all(
            completedCases.map(async (caseData) => {
              try {
                const clientId = caseData.clientId || caseData.userId;
                if (clientId) {
                  const { doc, getDoc } = await import('firebase/firestore');
                  const clientDoc = await getDoc(doc(db, 'users', clientId));
                  if (clientDoc.exists()) {
                    const clientData = clientDoc.data();
                    const clientName = clientData.full_name || 
                                     clientData.fullName || 
                                     clientData.displayName || 
                                     clientData.name || 
                                     (clientData.firstName && clientData.lastName ? `${clientData.firstName} ${clientData.lastName}` : null) ||
                                     'Client';
                    const clientPhoto = clientData.profile_picture || 
                                      clientData.profilePic || 
                                      clientData.photoURL || 
                                      clientData.avatar || 
                                      'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=40&h=40&fit=crop&crop=face';
                    
                    return {
                      ...caseData,
                      clientName: clientName,
                      clientPhoto: clientPhoto,
                      clientCity: clientData.custom_city || clientData.city || 'Location not available'
                    };
                  }
                }
                return caseData;
              } catch (error) {
                console.error('❌ Error enhancing completed case:', error);
                return caseData;
              }
            })
          );
          
          setCases(prev => ({ ...prev, completed: enhancedCompletedCases }));
          
          // Save to localStorage for persistence
          const allCases = {
            ...prev,
            completed: enhancedCompletedCases
          };
          localStorage.setItem(`lawyerCases_${user.uid}`, JSON.stringify(Object.values(allCases).flat()));
        });

      } catch (error) {
        console.error('❌ Error setting up real-time listeners:', error);
      }
    };

    setupRealTimeListeners();

            // Set loading to false after real-time listeners are set up
        setLoading(false);
        
        // Cleanup function
        return () => {
          console.log('🔌 Cleaning up real-time listeners...');
          if (unsubscribePending) unsubscribePending();
          if (unsubscribeActive) unsubscribeActive();
          if (unsubscribeCompleted) unsubscribeCompleted();
        };
  }, [isLawyer, user?.uid]);

  // Accept booking
  const handleAcceptBooking = async (bookingId) => {
    try {
      console.log('🔄 Accepting booking:', bookingId);
      const result = await bookingService.acceptBooking(bookingId);
      
      if (result.success) {
        toast.success(t.bookingAccepted);
        
        // Reload cases to reflect changes
        await loadLawyerCases();
        
        // Dispatch event to notify other components (including LawyerChats)
        window.dispatchEvent(new CustomEvent('bookingStatusChanged'));
        
        // Also dispatch a specific event for chat updates
        window.dispatchEvent(new CustomEvent('chatCreated', { 
          detail: { bookingId, chatCreated: result.chatCreated } 
        }));
        
        console.log('✅ Booking accepted successfully, chat created:', result.chatCreated);
      } else {
        toast.error(result.error || t.errorAccepting);
      }
    } catch (error) {
      console.error('Error accepting booking:', error);
      toast.error(t.errorAccepting);
    }
  };

  // Reject booking
  const handleRejectBooking = async (bookingId, reason = '') => {
    try {
      console.log('🔄 Rejecting booking:', bookingId);
      const result = await bookingService.rejectBooking(bookingId, reason);
      
      if (result.success) {
        toast.success(t.bookingRejected);
        
        // Reload cases to reflect changes
        await loadLawyerCases();
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('bookingStatusChanged'));
      } else {
        toast.error(result.error || t.errorRejecting);
      }
    } catch (error) {
      console.error('Error rejecting booking:', error);
      toast.error(t.errorRejecting);
    }
  };

  // Complete consultation
  const handleCompleteConsultation = async (bookingId) => {
    try {
      console.log('🔄 Completing consultation:', bookingId);
      const result = await completeConsultation(bookingId, user?.uid);
      
      if (result.success) {
        toast.success(t.consultationCompleted);
        
        // Reload cases to reflect changes
        await loadLawyerCases();
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('bookingStatusChanged'));
      } else {
        toast.error(result.error || t.errorCompleting);
      }
    } catch (error) {
      console.error('Error completing consultation:', error);
      toast.error(t.errorCompleting);
    }
  };

  // View client profile
  const handleViewClientProfile = async (clientId, clientName, bookingData) => {
    try {
      // Initialize clientProfileData at the top to avoid hoisting issues
      let clientProfileData = null;
      
      console.log('🔍 Loading client profile for:', clientId, clientName);
      console.log('📋 Booking data:', bookingData);
      console.log('🔍 Available fields in bookingData:', Object.keys(bookingData || {}));
      console.log('🔍 Full booking data structure:', JSON.stringify(bookingData, null, 2));
      console.log('🔍 userId from booking:', bookingData?.userId);
      console.log('🔍 consultationType from booking:', bookingData?.consultationType);
      console.log('🔍 consultation_mode from booking:', bookingData?.consultation_mode);
      console.log('🔍 All consultation related fields:', {
        consultationType: bookingData?.consultationType,
        consultation_mode: bookingData?.consultation_mode,
        consultationMode: bookingData?.consultationMode,
        type: bookingData?.type,
        consultation_type: bookingData?.consultation_type
      });
      console.log('🔍 All booking data fields:', Object.keys(bookingData || {}));
      console.log('🔍 City related fields:', {
        custom_city: clientProfileData?.custom_city,
        city: bookingData?.city,
        clientCity: bookingData?.clientCity
      });
      console.log('🔍 showClientProfile before:', showClientProfile);
      console.log('🔍 selectedClient before:', selectedClient);
      
      // Fetch client profile data from users collection
      if (bookingData?.userId) {
        try {
          console.log('🔍 Attempting to fetch client profile for userId:', bookingData.userId);
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('../config/firebase');
          const userDoc = await getDoc(doc(db, 'users', bookingData.userId));
          console.log('🔍 User document exists:', userDoc.exists());
          if (userDoc.exists()) {
            clientProfileData = userDoc.data();
            console.log('🔍 Fetched client profile data:', clientProfileData);
            console.log('🔍 All fields in clientProfileData:', Object.keys(clientProfileData));
            console.log('🔍 City field value:', clientProfileData?.custom_city);
            console.log('🔍 All city-related fields in clientProfileData:', {
              custom_city: clientProfileData?.custom_city,
              city: clientProfileData?.city,
              location: clientProfileData?.location,
              address: clientProfileData?.address,
              full_address: clientProfileData?.full_address,
              state: clientProfileData?.state,
              district: clientProfileData?.district
            });
            console.log('🔍 Full client data structure:', JSON.stringify(clientProfileData, null, 2));
          } else {
            console.log('❌ User document does not exist for userId:', bookingData.userId);
          }
        } catch (error) {
          console.error('❌ Error fetching client profile:', error);
        }
      } else {
        console.log('❌ No userId found in bookingData:', bookingData);
        // Try to get client data from booking data itself
        console.log('🔍 Checking if client data is embedded in booking data...');
        console.log('🔍 Client fields in booking:', {
          clientName: bookingData?.clientName,
          clientEmail: bookingData?.clientEmail,
          clientPhoto: bookingData?.clientPhoto,
          clientCity: bookingData?.clientCity,
          clientAddress: bookingData?.clientAddress
        });
      }
      
      // Create client profile with actual booking data and fetched profile data
      const clientProfile = {
        id: clientId,
        name: clientProfileData?.full_name || bookingData?.clientName || clientName || 'Client',
        phone: clientProfileData?.phone || bookingData?.clientPhone || '+91 99999 99999',
        city: clientProfileData?.custom_city || clientProfileData?.city || clientProfileData?.location || bookingData?.clientCity || bookingData?.city || bookingData?.location || 'City not specified',
        bio: clientProfileData?.bio || 'Client seeking legal consultation and guidance.',
        profilePic: clientProfileData?.profile_picture || bookingData?.clientPhoto || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=face',
        
        // Booking details
        bookingDetails: {
          bookingId: bookingData?.id,
          status: bookingData?.status,
          consultationType: bookingData?.consultationType,
          caseType: bookingData?.caseType,
          description: bookingData?.description,
          appointmentDate: bookingData?.appointmentDate,
          appointmentTime: bookingData?.appointmentTime,
          totalAmount: bookingData?.totalAmount || bookingData?.amount,
          consultationFees: bookingData?.consultationFees,
          serviceCharge: bookingData?.serviceCharge,
          isEmergency: bookingData?.isEmergency,
          createdAt: bookingData?.createdAt,
          lawyerAccepted: bookingData?.lawyerAccepted,
          lawyerRejected: bookingData?.lawyerRejected,
          consultationCompleted: bookingData?.consultationCompleted
        },
        
        consultationHistory: [],
                 preferences: {
           consultationMode: bookingData?.consultationType || 'General',
           preferredTime: bookingData?.appointmentTime || '08:06 AM',
           language: 'Not specified',
           specialRequirements: bookingData?.description || 'None'
         }
      };
      
      console.log('🔍 Created client profile:', clientProfile);
      console.log('🔍 Final city value being set:', clientProfile.city);
      console.log('🔍 City mapping breakdown:', {
        'clientProfileData?.custom_city': clientProfileData?.custom_city,
        'clientProfileData?.city': clientProfileData?.city,
        'clientProfileData?.location': clientProfileData?.location,
        'bookingData?.clientCity': bookingData?.clientCity,
        'bookingData?.city': bookingData?.city,
        'bookingData?.location': bookingData?.location,
        'final_city': clientProfile.city
      });
      setSelectedClient(clientProfile);
      setShowClientProfile(true);
      console.log('🔍 showClientProfile after set:', true);
      console.log('🔍 selectedClient after set:', clientProfile);
    } catch (error) {
      console.error('Error loading client profile:', error);
      toast.error('Error loading client profile');
    }
  };

  // Navigate to chat
  const handleNavigateToChat = (caseId, clientId, caseData) => {
    console.log('💬 Navigating to chat for case:', caseId, 'client:', clientId);
    navigate(`/lawyer-chat-conversation/${caseId}`, {
      state: { 
        caseData, 
        clientId,
        caseId: caseId
      }
    });
  };

  // Get consultation icon
  const getConsultationIcon = (mode) => {
    switch (mode?.toLowerCase()) {
      case 'call':
      case 'phone':
        return <Phone className="w-4 h-4" />;
      case 'video':
      case 'video call':
        return <Video className="w-4 h-4" />;
      case 'in person':
      case 'in-person':
        return <MapPin className="w-4 h-4" />;
      case 'text':
      case 'chat':
        return <MessageSquare className="w-4 h-4" />;
      default:
        return <Phone className="w-4 h-4" />;
    }
  };

  // Format date and time
  const formatDateTime = (date, time) => {
    if (!date) return 'Date not specified';
    
    try {
      const dateObj = new Date(date);
      
      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        return 'Invalid date';
      }
      
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      let dateString;
      if (dateObj.toDateString() === today.toDateString()) {
        dateString = 'Today';
      } else if (dateObj.toDateString() === tomorrow.toDateString()) {
        dateString = 'Tomorrow';
      } else {
        dateString = dateObj.toLocaleDateString('en-IN', {
          weekday: 'short',
          day: 'numeric',
          month: 'short'
        });
      }
      
      const formattedTime = time || '';
      return `${dateString} ${formattedTime}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date not specified';
    }
  };

  // Calculate lawyer's net amount after commission
  const calculateNetAmount = (totalAmount) => {
    const commission = (totalAmount * 20) / 100; // 20% platform commission
    return totalAmount - commission;
  };

  // Case Card Component
  const CaseCard = ({ caseData, showActions = false }) => {
    const isPending = caseData.status === 'pending';
    const isActive = caseData.status === 'active';
    const isCompleted = caseData.status === 'completed';
    const isEmergency = caseData.isEmergency || caseData.consultationType === 'emergency';

    // Emergency cases - Red theme
    if (isEmergency) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div 
              className="flex-1 cursor-pointer"
              onClick={() => {
                console.log('🔍 Clicked entire emergency case card');
                console.log('📋 Case data:', caseData);
                handleViewClientProfile(caseData.userId, caseData.clientName || caseData.full_name, caseData);
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-red-600" />
                {/* Client Profile Picture */}
                <img
                  src={caseData.clientPhoto || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=40&h=40&fit=crop&crop=face'}
                  alt={caseData.clientName || 'Client'}
                  className="w-8 h-8 rounded-full object-cover border-2 border-red-200"
                />
                <span className="font-semibold text-gray-800 text-sm">
                  {caseData.clientName || caseData.full_name || caseData.userName || 'Client'}
                </span>
                <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full font-medium">
                  EMERGENCY
                </span>
              </div>
              
              <h4 className="font-bold text-gray-800 mb-1 text-lg">
                {caseData.caseType || caseData.consultationType || 'Legal Consultation'}
              </h4>
              
              {caseData.description && (
                <p className="text-sm text-gray-500 mb-3">
                  {caseData.description}
                </p>
              )}
              
              <div className="flex items-center gap-1 text-sm text-red-600 mb-2 font-medium">
                <Clock className="w-4 h-4" />
                <span>{formatDateTime(caseData.appointmentDate, caseData.appointmentTime)}</span>
              </div>
            </div>
            
            <div className="flex flex-col items-end gap-2 ml-4">
              <div className="text-right">
                <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full flex items-center gap-1 mb-2">
                  {getConsultationIcon(caseData.consultationType)}
                  {caseData.consultationType}
                </span>
                <div className="text-xl font-bold text-gray-800">₹{caseData.totalAmount || caseData.amount}</div>
                <div className="text-xs text-gray-500">
                  Net: ₹{calculateNetAmount(caseData.totalAmount || caseData.amount)}
                </div>
              </div>
              
              <div className="flex flex-col gap-2">
                {isPending && showActions && (
                  <div className="flex flex-col gap-1.5">
                    <div className="flex gap-1.5">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAcceptBooking(caseData.id);
                        }}
                        className="px-2 py-1 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded-lg flex items-center gap-1 transition-colors"
                      >
                        <Check className="w-3 h-3" />
                        <span className="hidden sm:inline">Accept</span>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRejectBooking(caseData.id);
                        }}
                        className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg flex items-center gap-1 transition-colors"
                      >
                        <X className="w-3 h-3" />
                        <span className="hidden sm:inline">Reject</span>
                      </button>
                    </div>
                    <div className="text-xs text-gray-500 text-center">
                      Chat disabled until accepted
                    </div>
                  </div>
                )}
                {isActive && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCompleteConsultation(caseData.id);
                      }}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Complete
                    </button>
                    <button
                                        onClick={(e) => {
                    e.stopPropagation();
                    handleNavigateToChat(caseData.id, caseData.userId, caseData);
                  }}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Chat
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Active cases - Blue theme
    if (isActive) {
      return (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div 
              className="flex-1 cursor-pointer"
              onClick={() => {
                console.log('🔍 Clicked entire active case card');
                console.log('📋 Case data:', caseData);
                handleViewClientProfile(caseData.userId, caseData.clientName || caseData.full_name, caseData);
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-gray-700" />
                {/* Client Profile Picture */}
                <img
                  src={caseData.clientPhoto || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=40&h=40&fit=crop&crop=face'}
                  alt={caseData.clientName || 'Client'}
                  className="w-8 h-8 rounded-full object-cover border-2 border-blue-200"
                />
                <span className="font-semibold text-gray-800 text-sm">
                  {caseData.clientName || caseData.full_name || caseData.userName || 'Client'}
                </span>
                <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full">
                  Active
                </span>
              </div>
              
              <h4 className="font-bold text-gray-800 mb-1 text-lg">
                {caseData.caseType || caseData.consultationType || 'Legal Consultation'}
              </h4>
              
              {caseData.description && (
                <p className="text-sm text-gray-500 mb-3">
                  {caseData.description}
                </p>
              )}
              
              <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                <Calendar className="w-4 h-4" />
                <span>{formatDateTime(caseData.appointmentDate, caseData.appointmentTime)}</span>
              </div>
            </div>
            
            <div className="flex flex-col items-end gap-2 ml-4">
              <div className="text-right">
                <span className="text-xs bg-purple-100 text-purple-600 px-2 py-1 rounded-full flex items-center gap-1 mb-2">
                  {getConsultationIcon(caseData.consultationType)}
                  {caseData.consultationType}
                </span>
                <div className="text-xl font-bold text-gray-800">₹{caseData.totalAmount || caseData.amount}</div>
                <div className="text-xs text-gray-500">
                  Net: ₹{calculateNetAmount(caseData.totalAmount || caseData.amount)}
                </div>
              </div>
              
              <div className="flex flex-col gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCompleteConsultation(caseData.id);
                  }}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Complete
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNavigateToChat(caseData.id, caseData.userId, caseData);
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1"
                >
                  <MessageCircle className="w-4 h-4" />
                  Chat
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

         // Pending cases - Orange theme
     if (isPending) {
       return (
         <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 shadow-sm">
           <div className="flex items-start justify-between">
             <div 
               className="flex-1 min-w-0 cursor-pointer"
               onClick={() => {
                 console.log('🔍 Clicked entire pending case card');
                 console.log('📋 Case data:', caseData);
                 handleViewClientProfile(caseData.userId, caseData.clientName || caseData.full_name, caseData);
               }}
             >
               <div className="flex items-center gap-2 mb-2">
                                   {/* Client Profile Picture */}
                  <img
                    src={caseData.clientPhoto || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=40&h=40&fit=crop&crop=face'}
                    alt={caseData.clientName || 'Client'}
                    className="w-8 h-8 rounded-full object-cover border-2 border-orange-200"
                  />
                  <span className="font-semibold text-gray-900 text-sm">
                    {caseData.clientName || caseData.full_name || caseData.userName || 'Client'}
                  </span>
                 <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                   Pending
                 </span>
               </div>
               
               <h4 className="font-bold text-gray-900 mb-1 text-base">
                 {caseData.caseType || caseData.consultationType || 'Legal Consultation'}
               </h4>
               
               {caseData.description && (
                 <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                   {caseData.description}
                 </p>
               )}
               
               <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                 <Calendar className="w-3.5 h-3.5" />
                 <span className="font-medium">
                   {formatDateTime(caseData.appointmentDate, caseData.appointmentTime)}
                 </span>
               </div>
             </div>
             
             <div className="flex flex-col items-end gap-2 ml-3 flex-shrink-0">
               <div className="text-right">
                 <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full flex items-center gap-1 mb-1.5">
                   {getConsultationIcon(caseData.consultationType)}
                   <span className="hidden sm:inline">{caseData.consultationType}</span>
                 </span>
                 <div className="text-lg font-bold text-gray-900">₹{caseData.totalAmount || caseData.amount}</div>
                 <div className="text-xs text-gray-500">
                   Net: ₹{calculateNetAmount(caseData.totalAmount || caseData.amount)}
                 </div>
               </div>
               
               {showActions && (
                 <div className="flex flex-col gap-1.5">
                   <div className="flex gap-1.5">
                     <button
                       onClick={(e) => {
                         e.stopPropagation();
                         handleAcceptBooking(caseData.id);
                       }}
                       className="px-2 py-1 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded-lg flex items-center gap-1 transition-colors"
                     >
                       <Check className="w-3 h-3" />
                       <span className="hidden sm:inline">Accept</span>
                     </button>
                     <button
                       onClick={(e) => {
                         e.stopPropagation();
                         handleRejectBooking(caseData.id);
                       }}
                       className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg flex items-center gap-1 transition-colors"
                     >
                       <X className="w-3 h-3" />
                       <span className="hidden sm:inline">Reject</span>
                     </button>
                   </div>
                   <div className="text-xs text-gray-500 text-center">
                     Chat disabled until accepted
                   </div>
                 </div>
               )}
             </div>
           </div>
         </div>
       );
     }

    // Completed cases - Green theme
    if (isCompleted) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div 
              className="flex-1 cursor-pointer"
              onClick={() => {
                console.log('🔍 Clicked entire completed case card');
                console.log('📋 Case data:', caseData);
                handleViewClientProfile(caseData.userId, caseData.clientName || caseData.full_name, caseData);
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-gray-500" />
                {/* Client Profile Picture */}
                <img
                  src={caseData.clientPhoto || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=40&h=40&fit=crop&crop=face'}
                  alt={caseData.clientName || 'Client'}
                  className="w-8 h-8 rounded-full object-cover border-2 border-green-200"
                />
                <span className="font-semibold text-gray-900 text-sm">
                  {caseData.clientName || caseData.full_name || caseData.userName || 'Client'}
                </span>
                <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full">
                  Completed
                </span>
              </div>
              
              <div className="flex items-center gap-3 mb-3">
                <h4 className="font-bold text-gray-900">
                  {caseData.caseType || caseData.consultationType || 'Legal Consultation'}
                </h4>
                {caseData.description && (
                  <span className="text-sm text-gray-600">
                    {caseData.description}
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex flex-col items-end gap-2 ml-4">
              <div className="flex items-center gap-1">
                <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                  {getConsultationIcon(caseData.consultationType)}
                  {caseData.consultationType}
                </span>
                <div className="text-sm font-medium text-green-600">₹{caseData.totalAmount || caseData.amount}</div>
              </div>
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
          </div>
        </div>
      );
    }

    // Default case
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex items-start justify-between">
          <div 
            className="flex-1 cursor-pointer"
            onClick={() => {
              console.log('🔍 Clicked entire default case card');
              console.log('📋 Case data:', caseData);
              handleViewClientProfile(caseData.userId, caseData.clientName || caseData.full_name, caseData);
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-gray-500" />
                <span className="font-semibold text-gray-900 text-sm">
                  {caseData.clientName || 'Client'}
                </span>
              <span className="text-xs bg-purple-100 text-purple-600 px-2 py-1 rounded-full flex items-center gap-1">
                {getConsultationIcon(caseData.consultationType)}
                {caseData.consultationType}
              </span>
            </div>
            
            <h4 className="font-bold text-gray-900 mb-1 text-lg">
              {caseData.caseType || caseData.consultationType || 'Legal Consultation'}
            </h4>
            
            {caseData.description && (
              <p className="text-sm text-gray-600 mb-3">
                {caseData.description}
              </p>
            )}
            
            <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
              <Calendar className="w-4 h-4" />
              <span>{formatDateTime(caseData.appointmentDate, caseData.appointmentTime)}</span>
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-2 ml-4">
            <div className="text-right">
              <div className="text-xl font-bold text-gray-900">₹{caseData.totalAmount || caseData.amount}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-purple-600" />
            <p className="text-gray-600">{t.loadingCases}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/lawyer-dashboard')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">LC</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">NyaySahayak</h1>
              <p className="text-sm text-gray-600">Lawyer Dashboard</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={() => {
                console.log('🔄 Manual refresh clicked');
                loadLawyerCases();
              }}
              className="flex items-center gap-1 px-2 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs"
              title="Refresh cases from database"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>

            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-xs font-medium text-gray-700">Online</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex space-x-1 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-shrink-0 py-3 px-4 rounded-lg font-medium text-sm transition-colors ${
                activeTab === 'all'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All Cases ({cases.emergency.length + cases.active.length + cases.pending.length + cases.completed.length + cases.rescheduled.length})
            </button>
            <button
              onClick={() => setActiveTab('emergency')}
              className={`flex-shrink-0 py-3 px-4 rounded-lg font-medium text-sm transition-colors ${
                activeTab === 'emergency'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Emergency ({cases.emergency.length})
            </button>
            <button
              onClick={() => setActiveTab('active')}
              className={`flex-shrink-0 py-3 px-4 rounded-lg font-medium text-sm transition-colors ${
                activeTab === 'active'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Active ({cases.active.length})
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-shrink-0 py-3 px-4 rounded-lg font-medium text-sm transition-colors ${
                activeTab === 'pending'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Pending ({cases.pending.length})
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`flex-shrink-0 py-3 px-4 rounded-lg font-medium text-sm transition-colors ${
                activeTab === 'completed'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Completed ({cases.completed.length})
            </button>
          </div>
        </div>
      </div>

      {/* Content based on active tab */}
      {(activeTab === 'all' || activeTab === 'emergency') && cases.emergency.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-6"
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-red-200">
            <div className="flex items-center gap-2 mb-5">
              <Zap className="w-6 h-6 text-red-500" />
              <h3 className="text-xl font-bold text-red-900 dark:text-red-100">
                Emergency Consultations
              </h3>
              <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full font-medium">
                URGENT
              </span>
            </div>
            <div className="space-y-4">
              {cases.emergency.map((caseData) => (
                <CaseCard key={caseData.id} caseData={caseData} />
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {(activeTab === 'all' || activeTab === 'active') && cases.active.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-6"
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-5">
              {t.activeCases}
            </h3>
            <div className="space-y-4">
              {cases.active.map((caseData) => (
                <CaseCard key={caseData.id} caseData={caseData} />
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {(activeTab === 'all' || activeTab === 'pending') && cases.pending.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-6"
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-5">
              {t.pendingCases}
            </h3>
            <div className="space-y-4">
              {cases.pending.map((caseData) => (
                <CaseCard key={caseData.id} caseData={caseData} showActions={true} />
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {(activeTab === 'all' || activeTab === 'completed') && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-6"
        >
          <div className="bg-gray-50 rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <h3 className="text-2xl font-bold text-gray-900">{t.recentCompletedCases}</h3>
              </div>
              <p className="text-gray-600">Overview of your recently completed client consultations</p>
            </div>
            
            {cases.completed.length > 0 ? (
              <div className="space-y-0">
                {cases.completed.map((caseData) => (
                  <CaseCard key={caseData.id} caseData={caseData} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No completed cases yet</h3>
                <p className="text-gray-500">Completed consultations will appear here</p>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t.loadingCases}</h3>
          <p className="text-gray-500">Loading your cases...</p>
        </div>
      )}

      {/* Empty state when no cases */}
      {!loading && 
       cases.emergency.length === 0 && 
       cases.active.length === 0 && 
       cases.pending.length === 0 && 
       cases.completed.length === 0 && (
        <div className="text-center py-12">
          <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t.noCases}</h3>
          <p className="text-gray-500">You don't have any cases yet. They will appear here when clients book consultations.</p>
        </div>
      )}

      {/* Client Profile Modal */}
      {showClientProfile && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                         {/* Header */}
             <div className="flex items-center justify-between p-6 border-b border-gray-200">
               <h2 className="text-2xl font-bold text-gray-900">Client Profile</h2>
               <button
                 onClick={() => setShowClientProfile(false)}
                 className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
               >
                 <X className="w-6 h-6 text-gray-600" />
               </button>
             </div>

            {/* Profile Content */}
            <div className="p-6">
                             {/* Basic Info */}
               <div className="flex items-start gap-6 mb-8">
                 <img
                   src={selectedClient.profilePic}
                   alt={selectedClient.name}
                   className="w-24 h-24 rounded-full object-cover border-4 border-purple-100"
                 />
                 <div className="flex-1">
                   <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedClient.name}</h3>
                   <div className="space-y-2 text-sm text-gray-600">
                     <div className="flex items-center gap-2">
                       <MapPin className="w-4 h-4" />
                       <span>{selectedClient.city}</span>
                     </div>
                   </div>
                 </div>
               </div>

                             {/* Bio */}
               <div className="mb-8">
                 <h4 className="text-lg font-semibold text-gray-900 mb-3">About</h4>
                 <p className="text-gray-600 leading-relaxed">{selectedClient.bio}</p>
               </div>

               {/* Booking Details */}
               <div className="mb-8">
                 <h4 className="text-lg font-semibold text-gray-900 mb-4">Booking Details</h4>
                 <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-200">
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                     {/* Left Column */}
                     <div className="space-y-4">
                       <div>
                         <h5 className="font-semibold text-gray-900 mb-2">Consultation Information</h5>
                         <div className="space-y-2">
                           <div className="flex justify-between">
                             <span className="text-gray-600">Type:</span>
                             <span className="font-medium text-gray-900">{selectedClient.bookingDetails?.consultationType || 'Not specified'}</span>
                           </div>
                           <div className="flex justify-between">
                             <span className="text-gray-600">Case Type:</span>
                             <span className="font-medium text-gray-900">{selectedClient.bookingDetails?.caseType || 'Not specified'}</span>
                           </div>
                           <div className="flex justify-between">
                             <span className="text-gray-600">Status:</span>
                             <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                               selectedClient.bookingDetails?.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                               selectedClient.bookingDetails?.status === 'active' ? 'bg-green-100 text-green-700' :
                               selectedClient.bookingDetails?.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                               'bg-gray-100 text-gray-700'
                             }`}>
                               {selectedClient.bookingDetails?.status?.toUpperCase() || 'UNKNOWN'}
                             </span>
                           </div>
                           {selectedClient.bookingDetails?.isEmergency && (
                             <div className="flex justify-between">
                               <span className="text-gray-600">Priority:</span>
                               <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                 EMERGENCY
                               </span>
                             </div>
                           )}
                         </div>
                       </div>
                       
                       <div>
                         <h5 className="font-semibold text-gray-900 mb-2">Schedule</h5>
                         <div className="space-y-2">
                           <div className="flex justify-between">
                             <span className="text-gray-600">Date:</span>
                             <span className="font-medium text-gray-900">
                               {selectedClient.bookingDetails?.appointmentDate ? 
                                 new Date(selectedClient.bookingDetails.appointmentDate).toLocaleDateString('en-IN', {
                                   weekday: 'long',
                                   year: 'numeric',
                                   month: 'long',
                                   day: 'numeric'
                                 }) : 'Not specified'
                               }
                             </span>
                           </div>
                           <div className="flex justify-between">
                             <span className="text-gray-600">Time:</span>
                             <span className="font-medium text-gray-900">{selectedClient.bookingDetails?.appointmentTime || 'Not specified'}</span>
                           </div>
                           <div className="flex justify-between">
                             <span className="text-gray-600">Booked On:</span>
                             <span className="font-medium text-gray-900">
                               {selectedClient.bookingDetails?.createdAt ? 
                                 new Date(selectedClient.bookingDetails.createdAt.toDate?.() || selectedClient.bookingDetails.createdAt).toLocaleDateString('en-IN') : 'Not specified'
                               }
                             </span>
                           </div>
                         </div>
                       </div>
                     </div>
                     
                     {/* Right Column */}
                     <div className="space-y-4">
                       <div>
                         <h5 className="font-semibold text-gray-900 mb-2">Pricing</h5>
                         <div className="space-y-2">
                           <div className="flex justify-between">
                             <span className="text-gray-600">Consultation Fees:</span>
                             <span className="font-medium text-gray-900">₹{selectedClient.bookingDetails?.consultationFees || 0}</span>
                           </div>
                           <div className="flex justify-between">
                             <span className="text-gray-600">Service Charge:</span>
                             <span className="font-medium text-gray-900">₹{selectedClient.bookingDetails?.serviceCharge || 0}</span>
                           </div>
                           <div className="flex justify-between border-t pt-2">
                             <span className="text-gray-900 font-semibold">Total Amount:</span>
                             <span className="text-gray-900 font-bold text-lg">₹{selectedClient.bookingDetails?.totalAmount || 0}</span>
                           </div>
                           <div className="flex justify-between">
                             <span className="text-gray-600">Your Earnings (80%):</span>
                             <span className="font-medium text-green-600">₹{((selectedClient.bookingDetails?.totalAmount || 0) * 0.8).toFixed(2)}</span>
                           </div>
                         </div>
                       </div>
                       
                       <div>
                         <h5 className="font-semibold text-gray-900 mb-2">Case Description</h5>
                         <div className="bg-white rounded-lg p-3 border border-gray-200">
                           <p className="text-gray-700 text-sm leading-relaxed">
                             {selectedClient.bookingDetails?.description || 'No description provided by the client.'}
                           </p>
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>
               </div>

              

                             {/* Preferences */}
               <div className="mb-6">
                 <h4 className="text-lg font-semibold text-gray-900 mb-4">Client Preferences</h4>
                                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="font-medium text-gray-900 mb-2">Consultation Mode</div>
                      <div className="text-gray-600">
                        {selectedClient.preferences?.consultationMode === 'in-person' ? 'In-Person Consultation' :
                         selectedClient.preferences?.consultationMode === 'video' ? 'Video Consultation' :
                         selectedClient.preferences?.consultationMode === 'phone' ? 'Phone Consultation' :
                         selectedClient.preferences?.consultationMode === 'emergency' ? 'Emergency Consultation' :
                         selectedClient.preferences?.consultationMode || 'Not specified'}
                      </div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="font-medium text-gray-900 mb-2">Preferred Time</div>
                      <div className="text-gray-600">{selectedClient.preferences?.preferredTime || 'Not specified'}</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="font-medium text-gray-900 mb-2">Language</div>
                      <div className="text-gray-600">{selectedClient.preferences?.language || 'Not specified'}</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="font-medium text-gray-900 mb-2">Special Requirements</div>
                      <div className="text-gray-600">{selectedClient.preferences?.specialRequirements || 'None'}</div>
                    </div>
                  </div>
               </div>

              

               {/* Close Button */}
               <div className="mt-6 flex justify-center">
                 <button
                   onClick={() => setShowClientProfile(false)}
                   className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
                 >
                   Close
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}
    </div>
  );
};

export default LawyerCases;
